import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GlobalSearch from './components/GlobalSearch';
import AddCustomerModal from './components/AddCustomerModal';
import {
    Users, UserPlus, Car, ChevronRight,
    CalendarClock, CarFront, Stethoscope, FileText, CheckCircle2, X,
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import { reportError } from '../../../lib/errors';
import { LANE_META, useFollowUpCounts, type FollowUpLane } from './lists/useFollowUps';

interface CustomerStats {
    total_clients: number;
    total_vehicles: number;
    total_ltv: number;
    jobs_done: number;
    avg_job_value: number;
}

const formatCount = (n: number) => new Intl.NumberFormat('en-GB').format(n || 0);

/**
 * A count you can open.
 *
 * The money cards that used to sit alongside these are gone: Total Client LTV
 * was lifetime revenue with no time bound and no action attached, and Avg Job
 * Value is a pricing question, so it moved to Business Health where there is
 * room to explain it. Their tooltips were `group-hover:block`, which never
 * fires on a touch device — so on the primary device the two numbers that most
 * needed explaining were the two you could not get an explanation for.
 *
 * What is left is what a customer database should lead with: how many, and a
 * way in.
 */
function StatCard({
    icon: Icon,
    label,
    value,
    loading,
    onClick,
}: {
    icon: typeof Users;
    label: string;
    value: string;
    loading: boolean;
    onClick: () => void;
}) {
    return (
        // Two of these sit side by side on a phone, so at 320px each card has
        // ~100px of usable width. Side-by-side icon and figure does not fit —
        // the icon drops above the label until there is room for both.
        <button
            type="button"
            onClick={onClick}
            className="group bg-panel border border-line rounded-xl p-4 min-w-0 w-full text-left flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-4 hover:border-accent/40 hover:bg-tint/[0.02] transition-colors"
        >
            <div className="p-2.5 bg-accent/10 rounded-lg text-accent shrink-0">
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0 w-full">
                {/* No `truncate`: at 320px each of these cards is ~136px wide
                    and an uppercase tracked label plus a chevron does not fit on
                    one line, so "Customers" was clipping. It wraps instead. */}
                <p className="text-fd-3xs font-bold text-fg-2 uppercase tracking-wider">
                    {label}
                </p>
                {loading ? (
                    <div className="mt-1.5 h-6 w-20 rounded bg-raised animate-pulse" />
                ) : (
                    <p className="text-2xl font-bold text-fg tracking-tight mt-0.5 tabular-nums flex items-center gap-1">
                        {value}
                        {/* States that the card opens something. A bare number
                            is not an affordance — nobody taps a statistic on
                            spec. Sits beside the figure, which has the room. */}
                        <ChevronRight className="w-4 h-4 text-fg-2 shrink-0 group-hover:text-accent transition-colors" />
                    </p>
                )}
            </div>
        </button>
    );
}

const LANE_ICON: Record<FollowUpLane, typeof Users> = {
    mot: CalendarClock,
    visit: CarFront,
    advisory: Stethoscope,
    quote: FileText,
};

const LANES: FollowUpLane[] = ['mot', 'visit', 'advisory', 'quote'];

/** One follow-up lane. Same shape as the MOT Reminders row this replaces. */
function LaneRow({
    lane, count, loading, onClick,
}: { lane: FollowUpLane; count: number; loading: boolean; onClick: () => void }) {
    const Icon = LANE_ICON[lane];
    const meta = LANE_META[lane];
    const active = count > 0;
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full flex items-center gap-3 sm:gap-4 p-4 min-h-[44px] rounded-xl bg-panel border border-line hover:border-accent/40 hover:bg-tint/[0.02] transition-colors text-left group"
        >
            <div className={`p-2.5 rounded-lg shrink-0 ${
                active ? 'bg-accent/10 text-accent' : 'bg-tint/5 text-fg-4'
            }`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
                <p className={`text-sm font-bold ${active ? 'text-fg' : 'text-fg-4'}`}>
                    {meta.label}
                </p>
                {/* Wraps, not truncates — "Advisories you flagged that ar…" told you
                    nothing. Two short lines on a phone is fine. */}
                <p className="text-xs text-fg-2 mt-0.5">{meta.blurb}</p>
            </div>
            {loading ? (
                <div className="h-6 w-8 rounded bg-raised animate-pulse shrink-0" />
            ) : (
                <span className={`shrink-0 text-sm font-bold tabular-nums px-2.5 py-1 rounded-full ${
                    active ? 'bg-accent/15 text-accent' : 'text-fg-4'
                }`}>
                    {count}
                </span>
            )}
            <ChevronRight className="w-5 h-5 text-fg-2 group-hover:text-accent transition-colors shrink-0" />
        </button>
    );
}

export default function CustomersPage() {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    // Creating a customer used to leave the page — the modal navigated to a
    // route that did not exist, and the catch-all bounced the user to the
    // dashboard. Adding customers comes in runs, so the page now stays put and
    // confirms here, with a way through to the new profile when that is what
    // the user wanted.
    const [justCreatedId, setJustCreatedId] = useState<string | null>(null);
    const [statsVersion, setStatsVersion] = useState(0);
    const navigate = useNavigate();
    const { activeOrgId } = useVertical();
    const [stats, setStats] = useState<CustomerStats | null>(null);
    // Derived rather than a second flag kept in step by hand.
    const loadingStats = stats === null;
    const { counts, loading: loadingCounts } = useFollowUpCounts();

    useEffect(() => {
        if (!activeOrgId) return;
        let cancelled = false;
        void (async () => {
            const { data, error } = await supabase.rpc('fn_get_customer_stats', { p_org_id: activeOrgId });
            if (cancelled) return;
            if (error) reportError(error, 'customers-page.fn_get_customer_stats');
            const row = Array.isArray(data) ? data[0] : data;
            if (row) setStats(row as CustomerStats);
        })();
        return () => { cancelled = true; };
    }, [activeOrgId, statsVersion]);

    const totalFollowUps = LANES.reduce((n, l) => n + (counts[l] ?? 0), 0);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-line">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="p-3 bg-accent/10 rounded-xl shrink-0">
                        <Users className="w-6 h-6 text-accent" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-3xl max-md:text-2xl font-bold text-fg tracking-tight">
                            Customers
                        </h1>
                        <p className="text-fg-2 mt-1">
                            Search anyone by name or registration — or work through who's worth a call.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="w-full md:w-auto flex items-center justify-center gap-2 fd-btn-primary px-5 py-3 md:py-2.5 rounded-xl font-bold hover:scale-[1.02] transition-all active:scale-95"
                    >
                        <UserPlus className="w-5 h-5" />
                        <span>New Customer</span>
                    </button>
                </div>
            </div>

            {justCreatedId && (
                <div
                    role="status"
                    className="flex items-center gap-3 flex-wrap p-4 rounded-xl bg-success/10 border border-success/25"
                >
                    <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
                    <p className="min-w-0 flex-1 text-sm font-bold text-success">
                        Customer created.
                    </p>
                    <button
                        type="button"
                        onClick={() => navigate(`/clients/${justCreatedId}`)}
                        className="shrink-0 min-h-[44px] px-3 text-sm font-bold text-success hover:text-fg underline underline-offset-2"
                    >
                        Open profile
                    </button>
                    <button
                        type="button"
                        onClick={() => setJustCreatedId(null)}
                        aria-label="Dismiss"
                        className="shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center text-success/70 hover:text-fg"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Counts — both open a browsable list. The page could only SEARCH
                before, so if you did not already know who you wanted it gave
                you nothing. */}
            <div className="grid grid-cols-2 gap-4">
                <StatCard
                    icon={Users}
                    label="Customers"
                    value={formatCount(stats?.total_clients ?? 0)}
                    loading={loadingStats}
                    onClick={() => navigate('/clients/browse/customers')}
                />
                <StatCard
                    icon={Car}
                    label="Vehicles"
                    value={formatCount(stats?.total_vehicles ?? 0)}
                    loading={loadingStats}
                    onClick={() => navigate('/clients/browse/vehicles')}
                />
            </div>

            {/* Follow-ups — the work the workshop already knows about.
                Every one of these is derived from something a mechanic already
                recorded: an MOT date from a reg lookup, a service that has not
                repeated, an advisory typed into a health check, a quote that
                went quiet. None of it needed new data entry; it was all being
                captured and then never shown again. */}
            <section className="space-y-2">
                <div className="flex items-baseline justify-between gap-3">
                    <h2 className="text-fd-3xs font-bold text-fg-2 uppercase tracking-wider">
                        Follow-ups
                    </h2>
                    {!loadingCounts && totalFollowUps > 0 && (
                        <p className="text-xs text-fg-2">
                            {totalFollowUps} worth a call
                        </p>
                    )}
                </div>

                {!loadingCounts && totalFollowUps === 0 ? (
                    <div className="rounded-xl border border-line bg-panel p-6 text-center">
                        <p className="text-sm font-bold text-fg">Nothing to chase right now</p>
                        <p className="text-xs text-fg-2 mt-1.5 max-w-sm mx-auto">
                            MOTs coming due, customers who haven't been in for a year, advisories from past
                            health checks and unanswered quotes all show up here.
                        </p>
                    </div>
                ) : (
                    LANES.map((lane) => (
                        <LaneRow
                            key={lane}
                            lane={lane}
                            count={counts[lane] ?? 0}
                            loading={loadingCounts}
                            onClick={() => navigate(`/clients/follow-ups/${lane}`)}
                        />
                    ))
                )}
            </section>

            {/* The Mighty Search Bar */}
            <GlobalSearch className="transform hover:scale-[1.01] transition-transform duration-300" />

            {isCreateModalOpen && (
                <AddCustomerModal
                    onClose={() => setIsCreateModalOpen(false)}
                    onSuccess={(newCustomerId) => {
                        setJustCreatedId(newCustomerId);
                        setStatsVersion(v => v + 1);
                    }}
                />
            )}
        </div>
    );
}
